import { LayoutDashboard, MapPin } from 'lucide-react';
import { memo, useCallback, useEffect, useState } from 'react';
import { Badge, Modal, Skeleton } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { useDismissModalOnRouteChange } from '../hooks';
import { useDashboardData } from '../context';
import DashboardButton from './DashboardButton';

/**
 * Template preview — CT-5352.
 *
 * Fetches the template detail (composition + install requirements) on open
 * and hands the "Use template" action back to the caller, which owns the
 * actual `createDashboard({ templateId })` call and navigation.
 */
const INITIAL_PREVIEW_STATE = { detail: null, loading: false, error: false };

const TemplatePreviewModal = ({ open = false, template, onClose, onInstall, installing = false }) => {
  const { t } = useTranslation('dashboards');
  const { getTemplateById } = useDashboardData();
  const [{ detail, loading, error }, setPreviewState] = useState(INITIAL_PREVIEW_STATE);

  const templateId = template?.id ?? null;

  useEffect(() => {
    // Nothing to fetch — and the modal itself unmounts its body when closed,
    // so there is no stale content to clear here.
    if (!open || !templateId) return undefined;

    let cancelled = false;
    const loadTemplate = async () => {
      setPreviewState({ detail: null, loading: true, error: false });
      try {
        const result = await getTemplateById(templateId);
        if (cancelled) return;
        setPreviewState({ detail: result, loading: false, error: false });
      } catch {
        if (cancelled) return;
        setPreviewState({ detail: null, loading: false, error: true });
      }
    };

    loadTemplate();

    return () => {
      cancelled = true;
    };
  }, [open, templateId, getTemplateById]);

  useDismissModalOnRouteChange(open, onClose);

  const handleInstall = useCallback(() => {
    if (!template) return;
    onInstall?.(template);
  }, [onInstall, template]);

  const widgets = detail?.widgets ?? [];
  const requirements = detail?.requirements;
  const description = detail?.description || template?.description || '';
  const sectionPath = detail?.sectionPath || template?.sectionPath || '';
  const tags = detail?.tags ?? template?.tags ?? [];
  const hasRequirements =
    requirements && (requirements.requiredCapabilities?.length > 0 || requirements.minAppVersion);

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={template?.title || t('templatePreview.title')}
      size="md"
      portalRootId="shell-main-content"
      className="!max-w-[min(640px,calc(100%-2rem))]"
    >
      <div className="flex min-h-[280px] flex-col gap-5">
        {loading ? (
          <div className="flex flex-col gap-3">
            <Skeleton height="14px" width="100%" />
            <Skeleton height="14px" width="85%" />
            <Skeleton height="120px" width="100%" />
          </div>
        ) : error ? (
          <p className="text-sm text-danger">{t('templatePreview.error')}</p>
        ) : (
          <>
            {description && <p className="text-sm text-content-secondary">{description}</p>}

            <div className="flex flex-wrap items-center gap-3 text-xs text-content-muted">
              {sectionPath && (
                <span className="flex min-w-0 items-center gap-1">
                  <MapPin size={12} className="shrink-0" />
                  <span className="truncate">{sectionPath}</span>
                </span>
              )}
              <span className="flex shrink-0 items-center gap-1">
                <LayoutDashboard size={12} />
                {t('templatePreview.widgetCount', { count: widgets.length })}
              </span>
            </div>

            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <Badge key={tag} variant="lightBlue" size="small">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}

            {widgets.length > 0 && (
              <div className="flex flex-col gap-2">
                <h3 className="text-xs font-semibold uppercase text-content-muted">
                  {t('templatePreview.widgetsHeading')}
                </h3>
                <ul className="scrollbar-aimsos-auto-sm flex max-h-44 flex-col gap-1 overflow-y-auto">
                  {widgets.map((widget) => (
                    <li
                      key={widget.instanceId}
                      className="flex items-center justify-between gap-2 rounded-md border border-edge px-3 py-2"
                    >
                      <span className="truncate text-sm text-content-secondary">
                        {widget.name || t('templatePreview.untitledWidget')}
                      </span>
                      {widget.type && (
                        <Badge variant="default" size="small">
                          {widget.type}
                        </Badge>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {hasRequirements && (
              <div className="flex flex-col gap-2 rounded-lg border border-edge bg-surface-hover-subtle px-3 py-2">
                <h3 className="text-xs font-semibold uppercase text-content-muted">
                  {t('templatePreview.requirementsHeading')}
                </h3>
                {requirements.minAppVersion && (
                  <p className="text-xs text-content-secondary">
                    {t('templatePreview.minAppVersion', { version: requirements.minAppVersion })}
                  </p>
                )}
                {requirements.requiredCapabilities?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {requirements.requiredCapabilities.map((capability) => (
                      <Badge key={capability} variant="warning" size="small">
                        {capability}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <Modal.Footer>
        <DashboardButton variant="secondary" onClick={onClose} disabled={installing}>
          {t('templatePreview.cancelButton')}
        </DashboardButton>
        <DashboardButton onClick={handleInstall} disabled={installing || loading || Boolean(error) || !detail}>
          {installing ? t('templatePreview.installingButton') : t('templatePreview.installButton')}
        </DashboardButton>
      </Modal.Footer>
    </Modal>
  );
};

export default memo(TemplatePreviewModal);
